import React, { useState, useEffect, useMemo } from 'react'
import {
  Button,
  Divider,
  Heading,
  Typography,
  Frame,
  TextField,
  Checklist,
  TagsForm
} from 'components'
import { Box, makeStyles, Paper, Select, MenuItem, FormControl, InputLabel, Radio, RadioGroup, FormControlLabel, Chip } from '@material-ui/core'
import firebase from 'utils/firebase'
import { updateUser, updateRoles, useContent, getRolesFromMarkets, getIndustriesFromMarkets, useTags } from 'utils'
import AddSkillLinkDialog from 'components/freelancer/AddSkillLinkDialog'
import { SkillPill } from 'components/freelancer/FreelancerExpertiseSection'
import { uniqBy, debounce } from 'lodash'
import Fuse from 'fuse.js'
import AddRoleDialog from 'components/freelancer/AddRoleDialog'
import { ProgressBarFooter } from './FreelancerProgressBarFooter'

const useStyles = makeStyles((theme) => ({
  searchField: {
    marginBottom: theme.spacing(3)
  },
  yearInput: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    '& .MuiSelect-select': {
      textAlign: 'center',
      fontSize: '1.2rem'
    }
  },
  formControl: {
    width: '100%',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  experienceBox: {
    backgroundColor: '#FFF176', // Yellow color
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    borderRadius: theme.spacing(1),
    textAlign: 'center'
  },
  // Styles for crew/performer selection
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  radioButton: {
    '&.MuiRadio-root': {
      padding: 12,
      transform: 'scale(1.5)',
    },
    '& .MuiSvgIcon-root': {
      display: 'none',
    },
    '& .MuiIconButton-label:after': {
      content: '""',
      display: 'block',
      width: 18,
      height: 18,
      backgroundColor: 'white',
      border: '2px solid #999',
    },
    '&.Mui-checked .MuiIconButton-label:after': {
      backgroundColor: theme.palette.primary.main,
      border: '2px solid ' + theme.palette.primary.main,
    },
  },
  formControlLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: '1.25rem',
      fontWeight: 500,
      marginLeft: theme.spacing(3), // Add space between button and label
    },
    margin: theme.spacing(2, 0),
  },
  optionBox: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
    maxWidth: 200,
  },
  experienceNumber: {
    fontSize: '3rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary
  },
  yearsLabel: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
    fontSize: '1.2rem'
  },
  rolesContainer: {
    maxHeight: '400px',
    overflowY: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    padding: theme.spacing(1),
    marginBottom: theme.spacing(2)
  }
}))

// Generate year options starting from current year and going back 50 years
const generateYearOptions = (startYear, count) => {
  const years = []
  for (let i = 0; i < count; i++) {
    years.push(startYear - i)
  }
  return years
}

const FreelancerPrimaryRole = ({ user, markets, onNext, stepIndex, totalSteps, onboarding, history }) => {
  // Add history as prop, which will be provided by React Router
  const isEditing = !onboarding; // If not in onboarding flow, we're in editing mode
  const classes = useStyles()

  // Get tags for role skills
  const tags = useTags()

  // Industry selection state
  const [selectedIndustry, setSelectedIndustry] = useState(user?.industryId || '')
  const [industryName, setIndustryName] = useState(user?.industry || '')

  // For performers: appearance only
  const [performerStep, setPerformerStep] = useState(0) // 0=industry selection, 1=appearance

  // Appearance fields for performers
  const [heightValue, setHeightValue] = useState(user?.heightValue || '')
  const [heightUnit, setHeightUnit] = useState(user?.heightUnit || 'Feet')
  const [heightInches, setHeightInches] = useState(user?.heightInches || '')
  const [weightValue, setWeightValue] = useState(user?.weightValue || '')
  const [weightUnit, setWeightUnit] = useState(user?.weightUnit || 'Pounds')
  const [hairColor, setHairColor] = useState(user?.hairColor || '')
  const [eyeColor, setEyeColor] = useState(user?.eyeColor || '')

  // Primary role flow state
  const [currentStep, setCurrentStep] = useState(0) // 0 for crew/performer selection, 1 for role selection, 2 for year started, 3 for skills, 4 for hyperlinks
  const [selectedRole, setSelectedRole] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const { landing } = useContent() || {}
  const buttonColor = landing?.['onboarding-button-color'] || 'primary'
  const buttonSecondaryColor = landing?.['onboarding-button-secondary'] || '#6c757d'

  // Check if we have an editing role in sessionStorage
  const [isEditingExistingRole, setIsEditingExistingRole] = useState(false);

  // Ensure we start at the appropriate step when component is initialized
  useEffect(() => {
    // Check for editing role data in sessionStorage
    const editingRoleData = sessionStorage.getItem('editingRole');

    if (editingRoleData) {
      try {
        const roleData = JSON.parse(editingRoleData);
        // We're editing an existing role
        setIsEditingExistingRole(true);

        // Pre-fill industry data
        if (roleData.industryId) setSelectedIndustry(roleData.industryId);
        if (roleData.industry) setIndustryName(roleData.industry);

        // Pre-fill role data
        setSelectedRole(roleData);

        // Pre-fill year started/experience
        if (roleData.yearStarted) setYearStarted(roleData.yearStarted);

        // Pre-fill skills
        if (roleData.skills) setSelectedSkills(roleData.skills);

        // Pre-fill links
        if (roleData.links) setSkillLinks(roleData.links);

        // Start at role selection step
        setCurrentStep(1);
      } catch (error) {
        console.error('Error parsing editing role data:', error);
        // If error, just start at the beginning
        setCurrentStep(0);
        setPerformerStep(0);
      }

      // Clear the sessionStorage after using it
      sessionStorage.removeItem('editingRole');
    } else {
      // If not editing an existing role, start at the beginning
      setCurrentStep(0);
      setPerformerStep(0);
    }
  }, [])

  // Get roles for the selected industry from markets - memoized to prevent render loops
  const allRoles = useMemo(() => getRolesFromMarkets(markets), [markets])
  const industryRoles = useMemo(
    () => allRoles.filter(role => role.industryId === selectedIndustry),
    [allRoles, selectedIndustry]
  )

  // Get current year for default value
  const currentYear = new Date().getFullYear()
  const [yearStarted, setYearStarted] = useState(currentYear)

  // Generate 50 year options starting from current year
  const yearOptions = useMemo(() => generateYearOptions(currentYear, 50), [currentYear])

  // Calculate years of experience
  const yearsOfExperience = currentYear - yearStarted

  // State for skills selection
  const [selectedSkills, setSelectedSkills] = useState([])

  // State for skill links and dialog
  const [skillLinks, setSkillLinks] = useState({})
  const [roleToLink, setRoleToLink] = useState(null)

  const handleIndustryChange = (industry) => {
    setSelectedIndustry(industry.id)
    setIndustryName(industry.label)
  }

  // After industry selection, either go to appearance or directly to role selection
  const handleIndustryNext = async () => {
    // Data to save
    const updateData = { industryId: selectedIndustry, industry: industryName }

    // Save industry selection first
    await updateUser(user, updateData);

    // If performer industry, show appearance step in onboarding
    const isPerformerIndustry = industryName === 'Performers';
    if (isPerformerIndustry && onboarding) {
      // For performers in onboarding, go directly to appearance
      setPerformerStep(1);
    } else {
      // For crew or edit mode, go directly to role selection
      setCurrentStep(1);
    }
  }


  // After appearance, go to role selection
  const handleAppearanceNext = async () => {
    // Save appearance details
    const appearanceData = {};
    if (heightValue) appearanceData.heightValue = heightValue;
    if (heightUnit) appearanceData.heightUnit = heightUnit;
    if (heightUnit === 'Feet' && heightInches) appearanceData.heightInches = heightInches;
    if (weightValue) appearanceData.weightValue = weightValue;
    if (weightUnit) appearanceData.weightUnit = weightUnit;
    if (hairColor) appearanceData.hairColor = hairColor;
    if (eyeColor) appearanceData.eyeColor = eyeColor;

    await updateUser(user, appearanceData);

    // Move to role selection
    setCurrentStep(1);
  }

  // After completing all performer steps, move to role selection
  const handleCrewPerformerNext = () => {
    setCurrentStep(1);
  }

  const handleRoleNext = () => {
    if (!selectedRole) return
    setCurrentStep(2)
  }

  const handleYearNext = () => {
    if (!yearStarted) return
    setCurrentStep(3)
  }

  const handleFinalNext = async () => {
    if (!selectedRole || !yearStarted) return

    try {
      // console.log('Updating user with role data:', selectedRole, yearStarted, yearsOfExperience);
      // console.log('Selected skills for role:', selectedSkills);
      // console.log('Skill links:', skillLinks);

      // Get industry data from current selection or from user's current industry
      const industryData = {
        id: selectedIndustry || user.industryId,
        label: industryName || user.industry
      };

      // Create a role object with the same structure as FreelancerAddRole
      const roleData = {
        // These fields match what FreelancerAddRole saves
        roleId: selectedRole.roleId,
        label: selectedRole.label,
        experience: yearsOfExperience,
        yearStarted: yearStarted,
        isPrimary: isEditingExistingRole ? (selectedRole.isPrimary === true) : true,
        skills: selectedSkills || [], // Add skills to the role, ensure it's an array
        links: skillLinks || {} // Add links to skills
      };

      // If we're editing an existing role, preserve the original ID
      if (isEditingExistingRole && selectedRole.id) {
        // console.log('Updating existing role with ID:', selectedRole.id);
        roleData.id = selectedRole.id;
      }

      // Add industry data if available
      if (industryData.id) {
        roleData.industry = industryData.label;
        roleData.industryId = industryData.id;
        roleData.industryIds = [industryData.id];
      }

      // IMPORTANT: Use updateRoles to save the role in the same map format as FreelancerRoleList
      await updateRoles(user, roleData);

      // console.log(`Role successfully ${isEditingExistingRole ? 'updated' : 'created'} using updateRoles`);

      // Only update primary role fields if this is set as primary role
      if (roleData.isPrimary) {
        await updateUser(user, {
          primaryRoleId: selectedRole.roleId,
          primaryRoleLabel: selectedRole.label,
          primaryRoleYearStarted: yearStarted,
          primaryRoleExperience: yearsOfExperience
        });
      }

      firebase.analytics().logEvent(isEditingExistingRole ? 'user_role_updated' : 'user_role_added', {
        role: selectedRole.label,
        years_experience: yearsOfExperience
      });

      // Handle different navigation paths based on whether we're in edit mode or onboarding
      if (isEditing) {
        // If in edit mode, redirect to the manage roles page
        history.push('/roles/manage');
      } else if (onNext) {
        // If in onboarding mode, call the onNext function
        onNext();
      }
    } catch (error) {
      console.error('Error updating role:', error);
    }
  }

  const handleRoleChange = (items) => {
    const [role] = items
    if (role) {
      setSelectedRole(role)
    }
  }

  const handleYearChange = (e) => {
    setYearStarted(parseInt(e.target.value, 10))
  }

  const handleSkillsChange = (data) => {
    const skills = data.skills || []
    setSelectedSkills(skills)
    // console.log('Updated skills:', skills)
  }

  const handleSkillsNext = () => {
    if (selectedSkills.length > 0) {
      setCurrentStep(4); // Move to hyperlinks step
    } else {
      // If no skills selected, skip the hyperlinks step
      handleFinalNext();
    }
  }

  const handleBack = () => {
    if (currentStep === 0) {
      // Handle back navigation in performer sub-steps
      if (performerStep === 1) {
        setPerformerStep(0) // From appearance to industry selection
      }
    } else if (currentStep === 4) {
      setCurrentStep(3) // From hyperlinks to skills
    } else if (currentStep === 3) {
      setCurrentStep(2) // From skills to year started
    } else if (currentStep === 2) {
      setCurrentStep(1) // From year started to role selection
    } else if (currentStep === 1) {
      // If performer in onboarding, go back to appearance step
      if (industryName === 'Performers' && onboarding) {
        setCurrentStep(0)
        setPerformerStep(1)
      } else {
        // Otherwise go back to industry selection
        setCurrentStep(0)
        setPerformerStep(0)
      }
    }
  }

  // Handle adding a link to a skill
  const handleAddLink = (skill) => {
    // Get current link value if it exists
    const currentLink = skillLinks[skill] || '';

    setRoleToLink({
      role: {
        ...selectedRole,
        skills: selectedSkills,
        links: skillLinks
      },
      skill: skill,
      initialValue: currentLink
    });
  }

  // Handle saving link from dialog
  const handleSaveLink = (link) => {
    const { skill } = roleToLink;
    setSkillLinks({
      ...skillLinks,
      [skill]: link
    });
    setRoleToLink(null);
  }

  // Handle removing a link
  const handleRemoveLink = () => {
    const { skill } = roleToLink;
    const updatedLinks = { ...skillLinks };
    delete updatedLinks[skill];
    setSkillLinks(updatedLinks);
    setRoleToLink(null);
  }

  const handleLinksNext = () => {
    handleFinalNext();
  }

  // Determine if article should be 'a' or 'an'
  const getArticle = (word) => {
    return /^[aeiou]/i.test(word) ? 'an' : 'a'
  }

  // Determine which handler to use for navigation based on current step
  const getNextHandler = () => {
    if (currentStep === 0) {
      if (performerStep === 0) {
        return handleIndustryNext;
      } else if (performerStep === 1) {
        return handleAppearanceNext;
      }
    } else if (currentStep === 1) {
      return handleRoleNext;
    } else if (currentStep === 2) {
      return handleYearNext;
    } else if (currentStep === 3) {
      return handleSkillsNext;
    } else if (currentStep === 4) {
      return handleLinksNext;
    }
    return () => { };
  };

  // Determine if current step is valid for Next button
  const isCurrentStepValid = () => {
    if (currentStep === 0) {
      if (performerStep === 0) {
        return !!selectedIndustry;
      } else if (performerStep === 1) {
        return true; // Appearance step is always valid
      }
    } else if (currentStep === 1) {
      return !!selectedRole;
    } else if (currentStep === 2) {
      return !!yearStarted;
    } else if (currentStep === 3) {
      return selectedSkills.length > 0;
    } else if (currentStep === 4) {
      return true; // Links step is always valid
    }
    return false;
  };

  return (
    <Frame maxWidth="xs">
      <AddSkillLinkDialog
        open={!!roleToLink}
        onClose={() => setRoleToLink(null)}
        onRemove={handleRemoveLink}
        initialValue={roleToLink?.initialValue || ''}
        skill={roleToLink?.skill || ''}
        onSubmit={handleSaveLink}
      />
      {currentStep === 0 ? (
        // Different flows based on sub-steps for performer industry
        performerStep === 0 ? (
          // Step 0.0: Crew/Performer selection (Industry selection)
          <>
            <Heading subheading={isEditing ? null : `Step ${stepIndex} of ${totalSteps}`}>
              Select your industry
            </Heading>

            <Box my={3} display="flex" justifyContent="center" width="100%">
              <Box className={classes.radioGroup}>
                {markets && getIndustriesFromMarkets(markets).map(industry => (
                  <Box key={industry.id} display="flex" justifyContent="center" width="100%">
                    <Box className={classes.optionBox}>
                      <FormControlLabel
                        className={classes.formControlLabel}
                        value={industry.id}
                        control={<Radio
                          color="primary"
                          className={classes.radioButton}
                          checked={selectedIndustry === industry.id}
                          onChange={() => handleIndustryChange(industry)}
                        />}
                        label={industry.label}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            <Typography variant="body2" style={{ textAlign: 'center', marginBottom: 24 }}>
              Select whether you work on the performer side (actors, dancers, etc.) or crew side (directors, camera, etc.)
            </Typography>

            <Divider />

            <Box mx="auto" my={3} maxWidth={280}>
              <Button
                variant="contained"
                type="submit"
                style={{ width: '100%', 'background-color': buttonColor }}
                onClick={handleIndustryNext}
                disabled={!selectedIndustry}
              >
                Next
              </Button>
            </Box>
          </>
        ) : performerStep === 1 ? (
          // Step 0.1: Performer Appearance Details
          <>
            <Heading subheading={isEditing ? null : `Step ${stepIndex} of ${totalSteps}`}>
              Please give us details about your appearance.
            </Heading>

            <Box display="flex" flexDirection="column" alignItems="center" width="100%" maxWidth={480} mx="auto">
              {/* Height Row */}
              <Box my={2} display="flex" alignItems="center" width="100%">
                <Box width="120px" textAlign="right" pr={2}>
                  <Typography variant="subtitle1">
                    Height
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" flexGrow={1}>
                  <Box flexGrow={1} mr={2}>
                    <TextField
                      type="number"
                      value={heightValue}
                      onChange={(e) => setHeightValue(e.target.value)}
                      min={0}
                      style={{ width: '100%' }}
                    />
                  </Box>
                  <Box minWidth={100}>
                    <Select
                      value={heightUnit}
                      onChange={(e) => setHeightUnit(e.target.value)}
                      style={{ width: '100%' }}
                    >
                      <MenuItem value="Feet">Feet</MenuItem>
                      <MenuItem value="cm">cm</MenuItem>
                    </Select>
                  </Box>
                  {heightUnit === 'Feet' && (
                    <Box ml={2} minWidth={100}>
                      <TextField
                        label="Inches"
                        type="number"
                        value={heightInches}
                        onChange={(e) => setHeightInches(e.target.value)}
                        min={0}
                        max={11}
                        style={{ width: '100%' }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Weight Row */}
              <Box my={2} display="flex" alignItems="center" width="100%">
                <Box width="120px" textAlign="right" pr={2}>
                  <Typography variant="subtitle1">
                    Weight
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" flexGrow={1}>
                  <Box flexGrow={1} mr={2}>
                    <TextField
                      type="number"
                      value={weightValue}
                      onChange={(e) => setWeightValue(e.target.value)}
                      min={0}
                      style={{ width: '100%' }}
                    />
                  </Box>
                  <Box minWidth={100}>
                    <Select
                      value={weightUnit}
                      onChange={(e) => setWeightUnit(e.target.value)}
                      style={{ width: '100%' }}
                    >
                      <MenuItem value="Pounds">Pounds</MenuItem>
                      <MenuItem value="Kg">Kg</MenuItem>
                    </Select>
                  </Box>
                </Box>
              </Box>

              {/* Hair Color Row */}
              <Box my={2} display="flex" alignItems="center" width="100%">
                <Box width="120px" textAlign="right" pr={2}>
                  <Typography variant="subtitle1">
                    Hair Colour
                  </Typography>
                </Box>
                <Box flexGrow={1}>
                  <TextField
                    value={hairColor}
                    onChange={(e) => setHairColor(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </Box>
              </Box>

              {/* Eye Color Row */}
              <Box my={2} display="flex" alignItems="center" width="100%">
                <Box width="120px" textAlign="right" pr={2}>
                  <Typography variant="subtitle1">
                    Eye Colour
                  </Typography>
                </Box>
                <Box flexGrow={1}>
                  <TextField
                    value={eyeColor}
                    onChange={(e) => setEyeColor(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </Box>
              </Box>
            </Box>

            <Divider />

            <Box display="flex" justifyContent="space-between" mx="auto" my={3} maxWidth={280}>
              <Button
                variant="contained"
                style={{ width: '48%', backgroundColor: buttonSecondaryColor, color: 'white' }}
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                variant="contained"
                type="submit"
                style={{ width: '48%', 'background-color': buttonColor }}
                onClick={handleAppearanceNext}
              >
                Next
              </Button>
            </Box>
          </>
        ) : null
      ) : currentStep === 1 ? (
        // Step 1: Choose role
        <>
          <Heading subheading={isEditing ? null : `Step ${stepIndex} of ${totalSteps}`}>
            Choose your primary role
          </Heading>
          <Typography variant="body2" style={{ marginBottom: 16 }}>
            You can add other roles later
          </Typography>

          <Typography
            style={{ cursor: 'pointer', textAlign: 'center', fontSize: 12, marginBottom: 16 }}
            onClick={() => setDialogOpen(true)}
          >
            Are we missing a role?
          </Typography>
          <AddRoleDialog onClose={() => setDialogOpen(false)} open={dialogOpen} />

          <RoleList
            initialState={selectedRole ? [selectedRole] : []}
            markets={markets}
            onChange={handleRoleChange}
            user={user}
          />

          <Divider />

          <Box mx="auto" my={3} maxWidth={280}>
            <Button
              variant="contained"
              type="submit"
              style={{ width: '100%', 'background-color': buttonColor }}
              onClick={handleRoleNext}
              disabled={!selectedRole}
            >
              Next
            </Button>
          </Box>
        </>
      ) : currentStep === 2 ? (
        // Step 2: Year started
        <>
          <Heading subheading={isEditing ? null : `Step ${stepIndex + 1} of ${totalSteps}`}>
            What year did you start as {getArticle(selectedRole.label)} {selectedRole.label}?
          </Heading>

          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel id="year-started-label">Year Started</InputLabel>
            <Select
              labelId="year-started-label"
              id="year-started"
              name="yearStarted"
              value={yearStarted}
              onChange={handleYearChange}
              className={classes.yearInput}
              label="Year Started"
              fullWidth
            >
              {yearOptions.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Paper elevation={2} className={classes.experienceBox}>
            <Typography className={classes.experienceNumber}>
              {yearsOfExperience}
            </Typography>
          </Paper>

          <Typography className={classes.yearsLabel}>
            years
          </Typography>

          <Divider />

          <Box display="flex" justifyContent="space-between" mx="auto" my={3} maxWidth={280}>
            <Button
              variant="contained"
              style={{ width: '48%', backgroundColor: buttonSecondaryColor, color: 'white' }}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              type="submit"
              style={{ width: '48%', 'background-color': buttonColor }}
              onClick={handleYearNext}
              disabled={!yearStarted}
            >
              Next
            </Button>
          </Box>
        </>
      ) : currentStep === 3 ? (
        // Step 3: Skills selection
        <>
          <Heading subheading={isEditing ? null : `Step ${stepIndex + 2} of ${totalSteps}`}>
            What skills do you have as {getArticle(selectedRole.label)} {selectedRole.label}?
          </Heading>

          <Typography variant="body2" style={{ marginBottom: 16 }}>
            Select up to 15 skills that best represent your expertise.
          </Typography>

          <Box my={3}>
            {tags ? (
              <TagsForm
                tags={tags}
                value={selectedSkills}
                role={selectedRole?.label || ''}
                industry={industryName || ''}
                onChange={handleSkillsChange}
                search={true}
                max={15}
              />
            ) : (
              <Typography variant="body2" color="textSecondary" align="center">
                Loading skills...
              </Typography>
            )}
          </Box>
          <Divider />

          <Box display="flex" justifyContent="space-between" mx="auto" my={3} maxWidth={280}>
            <Button
              variant="contained"
              style={{ width: '48%', backgroundColor: buttonSecondaryColor, color: 'white' }}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              type="submit"
              style={{ width: '48%', 'background-color': buttonColor }}
              onClick={handleSkillsNext} // Changed to handleSkillsNext
            >
              Next
            </Button>
          </Box>
        </>
      ) : (
        // Step 4: Add hyperlinks to skills
        <>
          <Heading subheading={isEditing ? null : `Step ${stepIndex + 3} of ${totalSteps}`}>
            Link your work samples
          </Heading>

          <Typography variant="body2" style={{ marginBottom: 16, textAlign: 'center' }}>
            Click on any skill to add a link to a sample of your work.
          </Typography>

          <Box mt={3} display="flex" flexWrap="wrap" justifyContent="center">
            {selectedSkills.map((skill, index) => {
              const hasLink = skillLinks && skillLinks[skill];
              return (
                <Chip
                  key={`${skill}-${index}`}
                  label={skill}
                  style={{
                    margin: '4px',
                    backgroundColor: hasLink ? '#4caf50' : '#e0e0e0',
                    cursor: 'pointer',
                    color: hasLink ? 'white' : 'rgba(0, 0, 0, 0.87)'
                  }}
                  onClick={() => handleAddLink(skill)}
                />
              );
            })}
          </Box>

          <Box mt={4} mb={2} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              This step is optional. You can always add links later.
            </Typography>
          </Box>

          <Divider />

          <Box display="flex" justifyContent="space-between" mx="auto" my={3} maxWidth={280}>
            <Button
              variant="contained"
              style={{ width: '48%', backgroundColor: buttonSecondaryColor, color: 'white' }}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              type="submit"
              style={{ width: '48%', 'background-color': buttonColor }}
              onClick={handleLinksNext}
            >
              {isEditing ? 'Save' : 'Next'}
            </Button>
          </Box>
        </>
      )}

      {onboarding && (
        <ProgressBarFooter
          valid={isCurrentStepValid()}
          stepIndex={stepIndex}
          firstStep={stepIndex}
          numSteps={totalSteps}
          onNext={getNextHandler()}
          onPrev={handleBack}
        />
      )}
    </Frame>
  )
}

// Role search functionality from FreelancerAddRole.js
const debouncedSearchRoles = (markets) => {
  const roles = uniqBy(
    Object.values(markets)
      .map((m) => m.roles.sort((a, b) => a.name.localeCompare(b.name)))
      .flat(),
    (m) => m.id,
  )
  return debounce((query, callback) => {
    if (query === '')
      return callback(
        roles.map((r) => ({ roleId: r.id, label: r.name.trim() })),
      )
    const fuse = new Fuse(
      roles.map((r) => ({ roleId: r.id, label: r.name.trim() })),
      {
        keys: ['label'],
        minMatchCharLength: 2,
        distance: 100,
        threshold: 0.3,
      },
    )
    const results = fuse.search(query)
    callback(results.map((r) => r.item))
  }, 200)
}

// RoleList component from FreelancerAddRole.js
// Helper function to get industries from markets for a specific role ID
const getIndustriesByRoleId = (markets, roleId) =>
  uniqBy(
    Object.values(markets).filter((v) => v.roles.some((r) => r.id === roleId)),
    (m) => m.industryId,
  )
    .sort((a, b) => a.industry.localeCompare(b.industry))
    .map((m) => ({ label: m.industry, id: m.industryId }))

const RoleList = ({ markets, onChange, onSubmit, initialState, user }) => {
  const classes = useStyles()
  // Get the industry ID from props
  const userIndustryId = user?.industryId

  // Get roles for this industry from markets - memoized to prevent re-renders
  const allRoles = useMemo(() => getRolesFromMarkets(markets), [markets])
  const industryRoles = useMemo(
    () => allRoles.filter(role => role.industryId === userIndustryId),
    [allRoles, userIndustryId]
  )

  // Set items based on roles from the industry hook, formatted for the checklist
  const [items, setItems] = useState([])

  // Memoize the filter and format function
  const filterAndFormatRoles = useMemo(() => {
    return (searchQuery) => {
      if (!industryRoles || industryRoles.length === 0) {
        return [];
      }

      // If no query, return all roles
      if (!searchQuery) {
        return industryRoles
          .map(role => ({ roleId: role.id || role.roleId, label: role.name || role.label }))
          .sort((a, b) => a.label.localeCompare(b.label));
      }

      // Filter by the search query
      return industryRoles
        .filter(role => {
          const roleName = (role.name || role.label || '').toLowerCase();
          return roleName.includes(searchQuery.toLowerCase());
        })
        .map(role => ({ roleId: role.id || role.roleId, label: role.name || role.label }))
        .sort((a, b) => a.label.localeCompare(b.label));
    };
  }, [industryRoles]);

  // When we get roles from the API, format them for the checklist
  useEffect(() => {
    setItems(filterAndFormatRoles(''));
  }, [filterAndFormatRoles])

  return (
    <>
      <TextField
        name="query"
        label="Search"
        placeholder="Enter your role or scroll the list"
        className={classes.searchField}
        onChange={(e) => {
          const query = e.target.value
          setItems(filterAndFormatRoles(query));
        }}
      />

      <Box className={classes.rolesContainer}>
        <Checklist
          label="Next"
          onSubmit={onSubmit}
          onChange={onChange}
          initialState={initialState}
          items={items}
        />
      </Box>
    </>
  )
}

export default FreelancerPrimaryRole
